import { inject, Injectable, InjectionToken } from '@angular/core';
import { safeFetchAuthSession } from '@jepto/shared/utils/fetch-auth-session.util';
import { createAnalyticsDataRequestId } from './analytics-data-latency.service';

/** Resolves the Cognito principal for analytics data GraphQL (`requester`). */
export type AnalyticsDataRequesterResolver = () =>
	| Promise<string | null | undefined>
	| string
	| null
	| undefined;

/**
 * Optional app override. Analytics standalone uses Cognito Identity Pool `IdentityId`
 * for guest viewers; Jepto uses the default resolver (`sub` from the user-pool id token).
 */
export const ANALYTICS_DATA_REQUESTER_RESOLVER =
	new InjectionToken<AnalyticsDataRequesterResolver>(
		'ANALYTICS_DATA_REQUESTER_RESOLVER'
	);

export interface AnalyticsDataRequestContext {
	requestId: string;
	requester: string;
}

export type AnalyticsWebSocketRequesterDecision =
	| 'accept'
	| 'reject'
	| 'pending';

/** Reads `requester` from a parsed AppSync card WebSocket payload. */
export function webSocketRequesterFromEvent(event: unknown): string | undefined {
	if (!event || typeof event !== 'object' || !('requester' in event)) {
		return undefined;
	}
	const requester = (event as { requester?: unknown }).requester;
	if (typeof requester !== 'string') {
		return undefined;
	}
	const trimmed = requester.trim();
	return trimmed ? trimmed : undefined;
}

@Injectable({ providedIn: 'root' })
export class AnalyticsDataRequesterService {
	private readonly customResolver = inject(ANALYTICS_DATA_REQUESTER_RESOLVER, {
		optional: true,
	});

	private cachedRequester: string | null = null;
	private cachedRequesterPromise: Promise<string> | null = null;

	getCachedRequester(): string | null {
		return this.cachedRequester;
	}

	isRequesterReady(): boolean {
		return Boolean(this.cachedRequester?.trim());
	}

	/**
	 * Gate WebSocket card data so concurrent viewers do not overwrite each other's
	 * charts. When `requester` is omitted the update is global and always accepted.
	 * When set, accept only if it matches our cached principal; `pending` until
	 * the cache is warm for scoped updates.
	 */
	evaluateWebSocketRequester(
		incomingRequester?: string | null
	): AnalyticsWebSocketRequesterDecision {
		const incoming = incomingRequester?.trim();
		if (!incoming) {
			return 'accept';
		}
		const mine = this.cachedRequester?.trim();
		if (!mine) {
			return 'pending';
		}
		return incoming === mine ? 'accept' : 'reject';
	}

	/** Cognito user-pool `sub` when logged in; overridden per app for anonymous analytics. */
	async resolveRequester(): Promise<string> {
		if (!this.cachedRequesterPromise) {
			this.cachedRequesterPromise = this.#fetchRequester();
		}
		const result = await this.cachedRequesterPromise;
		const trimmed = result?.trim();
		this.cachedRequester = trimmed ? trimmed : null;
		return trimmed ?? '';
	}

	async #fetchRequester(): Promise<string> {
		if (this.customResolver) {
			const custom = await this.customResolver();
			const trimmed = custom?.trim();
			if (trimmed) {
				return trimmed;
			}
		}

		const session = await safeFetchAuthSession();
		const sub = session?.tokens?.idToken?.payload?.sub;
		if (sub != null && String(sub).trim()) {
			return String(sub);
		}

		return '';
	}

	/** Client ULID for `getPageData` / `getCard` — set the WS floor before awaiting requester. */
	createRequestId(): string {
		return createAnalyticsDataRequestId();
	}

	async buildRequestContext(
		requestId: string = createAnalyticsDataRequestId()
	): Promise<AnalyticsDataRequestContext> {
		return {
			requestId,
			requester: await this.resolveRequester(),
		};
	}
}
